import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { Box, Button, LinearProgress, Tooltip } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store";
import FileUpload from "../../components/FileUpload";
import { getCustomerUploadStatus, getCustomerUploadData } from "../../store/selectors/CustomerUploadSelectors";
import { resetCustomerUpload } from "../../store/reducers/CustomerUploadReducer";
import { uploadCustomerImageAction } from "../../store/actions/CustomerUploadActions";
import InstructionOverlay from "../../components/InstructionOverlay/InstructionOverlay";
import InfoIcon from "@mui/icons-material/Info";
import { onRunPredictionAction } from "../../store/actions/PredictionActions";
import {
  getScrewPredictionData,
  getPredictionRunningState,
} from "../../store/selectors/PredictionSelectors";
import { resetPredictionSlice } from "../../store/reducers/PredictionReducers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getScrewsPropertiesAction } from "../../store/actions/ScrewsActions";
import { getScrewProperties } from "../../store/selectors/ScrewSelectors";
import type { CategoricalValueOption, ScrewProperty } from "../../types/ScrewTypes";
import { api } from "../../services/Api";
import "./Predict.scss";

const SkipFormEffect: React.FC<{ onMount: () => void }> = memo(({ onMount }) => {
  useEffect(() => { onMount(); }, [onMount]);
  return null;
});

interface UserInputEntry {
  propertyid: string;
  propertyname: string;
  categoricalvalueid: string;
  categoricalvalue: string;
}

interface UserInputPayload {
  properties: UserInputEntry[];
  notes: string;
}

const EXCLUDED_PROPERTIES = ["ref", "platform"];
const DROPDOWN_PROPERTIES = ["seating"];
const PROPERTY_LABEL_OVERRIDES: Record<string, string> = { thread: "Thread-diameter" };

const getPropertyDisplayLabel = (propertyname: string) =>
  PROPERTY_LABEL_OVERRIDES[propertyname.toLowerCase()] ?? propertyname;

const ScrewPropertiesForm: React.FC<{
  clientuploadid: string;
  onDone: () => void;
}> = ({ clientuploadid, onDone }) => {
  const screwProperties = useAppSelector(getScrewProperties);
  const [values, setValues] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const sortOpts = (opts: CategoricalValueOption[]) =>
    opts
      .slice()
      .sort((a, b) =>
        (a.categoricalvalue ?? "").localeCompare(b.categoricalvalue ?? "", undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
      .map((o) => ({ value: o.categoricalvalueid, label: o.categoricalvalue ?? o.categoricalvalueid }));

  const editableProperties = useMemo<ScrewProperty[]>(() => {
    if (!screwProperties?.items) return [];
    return screwProperties.items
      .filter((p: ScrewProperty) => {
        const name = (p.propertyname ?? "").toLowerCase();
        if (EXCLUDED_PROPERTIES.includes(name)) return false;
        return (p.categoricalvalueoptions ?? []).length > 0;
      })
      .sort((a: ScrewProperty, b: ScrewProperty) =>
        (a.propertyname ?? "").localeCompare(b.propertyname ?? "")
      );
  }, [screwProperties]);

  const isDropdown = (propertyname: string) =>
    DROPDOWN_PROPERTIES.includes(propertyname.toLowerCase());

  const handleChange = (propertyid: string, value: string) => {
    setValues((prev) => ({ ...prev, [propertyid]: value }));
  };

  const handleSubmit = async (skip: boolean) => {
    setSubmitting(true);
    if (!skip) {
      const properties: UserInputEntry[] = editableProperties
        .map((p) => {
          const val = values[p.propertyid];
          if (!val) return null;
          if (isDropdown(p.propertyname)) {
            const opt = sortOpts(p.categoricalvalueoptions ?? []).find((o) => o.value === val);
            return {
              propertyid: p.propertyid,
              propertyname: p.propertyname,
              categoricalvalueid: val,
              categoricalvalue: opt?.label ?? val,
            };
          }
          return {
            propertyid: p.propertyid,
            propertyname: p.propertyname,
            categoricalvalueid: "",
            categoricalvalue: val,
          };
        })
        .filter(Boolean) as UserInputEntry[];

      const payload: UserInputPayload = { properties, notes: notes.trim() };
      try {
        await api.patch(`/client-uploads/${clientuploadid}/user-input`, {
          userinput: JSON.stringify(payload),
        });
      } catch {
        // Silently continue — user input is optional, don't block the flow
      }
    }
    setSubmitting(false);
    onDone();
  };

  return (
    <div className="screw-user-input-form">
      <div className="user-input-header">
        <h2>What do you know about this screw?</h2>
        <p>
          On this page, you can fill in what you already know about the screw. Every field is optional — leave it empty if you're unsure. This helps our team verify your submission faster.
        </p>
      </div>

      {!screwProperties ? (
        <Box sx={{ width: "100%", maxWidth: 400, margin: "24px auto" }}>
          <LinearProgress />
        </Box>
      ) : (
        <div className="user-input-grid">
          {editableProperties.map((property) => {
            const current = values[property.propertyid] ?? "";
            const dropdown = isDropdown(property.propertyname);
            const opts = dropdown ? sortOpts(property.categoricalvalueoptions ?? []) : [];
            return (
              <div key={property.propertyid} className="user-input-field">
                <label htmlFor={`prop-${property.propertyid}`} className="user-input-label">
                  {getPropertyDisplayLabel(property.propertyname)}
                  {property.description && (
                    <Tooltip title={property.description} arrow placement="top">
                      <InfoOutlinedIcon className="info-icon" fontSize="small" />
                    </Tooltip>
                  )}
                </label>
                {dropdown ? (
                  <select
                    id={`prop-${property.propertyid}`}
                    className="user-input-select"
                    value={current}
                    onChange={(e) => handleChange(property.propertyid, e.target.value)}
                  >
                    <option value="">Not sure</option>
                    {opts.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`prop-${property.propertyid}`}
                    type="text"
                    className="user-input-text"
                    placeholder="Not sure"
                    value={current}
                    onChange={(e) => handleChange(property.propertyid, e.target.value)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="user-input-notes">
        <label htmlFor="user-input-notes-field" className="user-input-label">
          Additional remarks <span className="optional-hint">(optional)</span>
        </label>
        <textarea
          id="user-input-notes-field"
          className="user-input-textarea"
          placeholder="Anything else you'd like to mention about this screw..."
          value={notes}
          rows={3}
          maxLength={500}
          onChange={(e) => setNotes(e.target.value)}
        />
        <span className="user-input-char-count">{notes.length}/500</span>
      </div>

      <div className="user-input-actions">
        <Button
          variant="text"
          className="btn-skip"
          disabled={submitting}
          onClick={() => handleSubmit(true)}
        >
          Skip — I don't know anything
        </Button>
        <Button
          variant="contained"
          className="btn btn-blue"
          disabled={submitting}
          onClick={() => handleSubmit(false)}
        >
          {submitting ? "Saving..." : "Continue"}
        </Button>
      </div>
    </div>
  );
};

const PredictScrews = () => {
  const dispatch = useAppDispatch();
  const uploadStatus = useAppSelector(getCustomerUploadStatus);
  const customerUploadData = useAppSelector(getCustomerUploadData);
  const predictionRunningState = useAppSelector(getPredictionRunningState);
  const screwPredictionData = useAppSelector(getScrewPredictionData);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInstructionOverlay, setShowInstructionOverlay] = useState(false);
  const skipFirstUploadStatusEffect = useRef(true);

  // Reset shared upload/prediction state on mount so we don't show step 2 or loading from a previous flow (e.g. Predict Locators).
  // Also pre-fetch screw properties so they are ready when the user reaches the form in step 2.
  useEffect(() => {
    setCurrentStep(1);
    setSelectedFile(null);
    dispatch(resetCustomerUpload());
    dispatch(resetPredictionSlice());
    dispatch(getScrewsPropertiesAction());
    return function cleanup() {
      setSelectedFile(null);
      dispatch(resetCustomerUpload());
      dispatch(resetPredictionSlice());
    };
  }, [dispatch]);

  const handleFileSubmission = useCallback(() => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("image", selectedFile, selectedFile.name);
    formData.append("model_type", "screw");
    dispatch(uploadCustomerImageAction(formData));
  }, [selectedFile, dispatch]);

  const fileUploadProps = useCallback(
    () => ({
      isFileUploading: uploadStatus === "loading",
      selectedFile,
      showCancel: false,
      onFileChange: setSelectedFile,
      onCancelUpload: () => console.log("Not implemented"),
      handleFileSubmission,
      modelType: "screw",
      allowMultiple: false,
    }),
    [selectedFile, uploadStatus, handleFileSubmission]
  );

  useEffect(() => {
    // Skip first run so we never react to stale "success" from the other predict page when switching.
    if (skipFirstUploadStatusEffect.current) {
      skipFirstUploadStatusEffect.current = false;
      return;
    }
    if (uploadStatus === "success") {
      // Start prediction polling in background while user fills in the form
      dispatch(onRunPredictionAction());
      setCurrentStep(2);
    }
  }, [uploadStatus, dispatch]);

  const handleFormDone = useCallback(() => {
    setCurrentStep(3);
  }, []);

  const renderPredictions = useCallback(() => {
    if (predictionRunningState === "loading") {
      return (
        <div className="prediction-loading">
          <Box sx={{ width: "100%", maxWidth: 400, margin: "0 auto" }}>
            <p>Our AI model is analyzing your image...</p>
            <LinearProgress />
          </Box>
        </div>
      );
    }

    if (predictionRunningState === "success" && screwPredictionData?.length) {
      return (
        <div className="prediction-results">
          <div className="prediction-disclaimer">
            <p>
              Our AI model has analyzed your image and provided potential matches. Please keep in mind that these are
              preliminary suggestions and <b>may not be entirely accurate</b>.
            </p>
            <p>
              Our team will review your submission and send a <b>verified response via email</b> within one business day.
            </p>
          </div>
          <div className="predictions-grid">
            {screwPredictionData.slice(0, 3).map((prediction, index) => {
              const VISIBLE_PROPERTIES = ["brand", "platform"];
              const visibleValues = (prediction.propertyvalues ?? []).filter((pv) =>
                VISIBLE_PROPERTIES.includes((pv.propertyname ?? "").toLowerCase())
              );
              return (
                <div key={index} className="prediction-card">
                  <div className="prediction-header">
                    <h3>Prediction {index + 1}</h3>
                  </div>
                  <div className="confidence">
                    Confidence: {prediction.confidence.toFixed(1)}%
                    <Tooltip
                      title="Confidence indicates how certain our AI model is about this match."
                      arrow
                      placement="top"
                      className="confidence-tooltip"
                    >
                      <InfoOutlinedIcon className="info-icon" fontSize="small" />
                    </Tooltip>
                  </div>
                  <div className="prediction-details">
                    {prediction.categoricalvalue && (
                      <p>
                        <strong>Ref:</strong> {prediction.categoricalvalue}
                      </p>
                    )}
                    {visibleValues.map((pv, i) => (
                      <p key={i}>
                        <strong>{pv.propertyname}:</strong>{" "}
                        {pv.categoricalvalue ?? (pv.numericalvalue != null ? String(pv.numericalvalue) : "-")}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  }, [predictionRunningState, screwPredictionData]);

  const handleReset = useCallback(() => {
    setCurrentStep(1);
    setSelectedFile(null);
    dispatch(resetCustomerUpload());
    dispatch(resetPredictionSlice());
  // dispatch is stable, no need to include it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageContent = useCallback(() => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <div className="instruction-button-wrapper">
              <Button
                variant="text"
                className="instruction-button animate"
                onClick={() => setShowInstructionOverlay(true)}
              >
                <InfoIcon />
                Instructions
              </Button>
            </div>
            {showInstructionOverlay && (
              <InstructionOverlay onClose={() => setShowInstructionOverlay(false)}>
                <p>Drag and drop an image of the screw, or click to select an image from your device.</p>
                <p>For best results:</p>
                <ul>
                  <li>Use a plain, neutral background</li>
                  <li>Ensure the screw is centered and clearly visible</li>
                  <li>Make sure no other objects are in the photo besides the screw</li>
                  <li>Avoid extreme camera angles and direct sunlight</li>
                  <li>Verify that the complete screw is visible</li>
                </ul>
                <p>Examples:</p>
                <div className="instruction-image-wrapper">
                  <img src="/images/screw-example-1.jpg" alt="Screw example 1" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  <img src="/images/screw-example-2.jpg" alt="Screw example 2" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  <img src="/images/screw-example-3.jpg" alt="Screw example 3" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                </div>
              </InstructionOverlay>
            )}
            <div className="predict-layout">
              <div className="predict-left-side">
                <div className="instruction-box good-picture">
                  <span>Good picture</span>
                </div>
                <div className="instruction-box only-one-locator">
                  <span>Only one screw<br />on the picture</span>
                </div>
                <div className="instruction-box white-background">
                  <span>Plain background</span>
                </div>
                <div className="instruction-box complete-locator">
                  <span>Complete screw<br />visible</span>
                </div>
              </div>
              <div className="predict-center">
                <FileUpload {...fileUploadProps()} />
              </div>
              <div className="predict-right-side">
                <div className="example-section">
                  <span className="example-label">Good example</span>
                  <div className="example-image">
                    <img src="/images/screw-example-4.jpg" alt="Screw example" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case 2:
        return customerUploadData?.clientuploadid ? (
          <ScrewPropertiesForm
            clientuploadid={customerUploadData.clientuploadid}
            onDone={handleFormDone}
          />
        ) : (
          // Fallback: upload data unavailable, skip the form
          <SkipFormEffect onMount={handleFormDone} />
        );
      case 3:
        return (
          <div className="upload-success-view">
            <div className="success-content">
              {(predictionRunningState === "loading" || !screwPredictionData?.length) && (
                <div className="success-message">
                  <img src="/images/SuccessIcon.jpg" alt="Success" className="success-icon" />
                  <h2>Thank you for your submission</h2>
                  <p>We will review your image and send you a verified response within one business day.</p>
                  <p>If you want, you can wait to see the results from our AI model. This may take a minute.</p>
                </div>
              )}
              {renderPredictions()}
              <Button
                variant="contained"
                className="btn btn-blue"
                onClick={handleReset}
              >
                Upload Another Image
              </Button>
            </div>
          </div>
        );
      default:
        return <Box>Something went wrong</Box>;
    }
  }, [currentStep, fileUploadProps, showInstructionOverlay, predictionRunningState, screwPredictionData, renderPredictions, customerUploadData, handleFormDone, handleReset]);

  return pageContent();
};

export default PredictScrews;
